import { Platform, Dimensions, Linking } from 'react-native'
import { ToastAndroid } from 'app/mocks/ToastAndroid'

import { clearStorage, removeCookies } from './storage'
import keycloak from './keycloak'
import { finishCurrentActivity } from './appConfig'
import { navigationRef } from './RootNavigation'
import Sentry from './sentry'

export const DIMENSION_WIDTH = Dimensions.get('window').width
export const DIMENSION_HEIGHT = Dimensions.get('window').height

export const isMobile = () => {
  return Platform.OS === 'android' || Platform.OS === 'ios'
}

export const isMobileDevice = () => {
  if (Platform.OS === 'web') {
    const userAgent = navigator.userAgent
    return /mobile/i.test(userAgent) || /tablet/i.test(userAgent) // Simple mobile device check
  }
  return true
}

export const backHandler = () => {
  try {
    if (navigationRef.canGoBack()) {
      navigationRef.goBack()
    } else {
      window.history.back()
    }
    return true
  } catch (e) {
    return false
  }
}

export const logout = async () => {
  const host = `${window.location.protocol}//${window.location.host}`
  try {
    localStorage.clear()
    clearAuthCookies()
    await clearStorage()
    await keycloak.logout({ redirectUri: host })
  } catch {
    // Fallback: if Keycloak SDK logout fails, force app reload after local cleanup.
    clearAuthCookies()
    await clearStorage()
    window.location.assign(host)
  }
}

export const clearAuthCookies = () => {
  removeCookies([
    'access_token',
    'refresh_token',
    'expires_in',
    'id_token',
    'refresh_expires_in',
  ])
}

export const dialNumber = (phoneNumber: string) => {
  let phoneUrl = `tel:${phoneNumber}`

  Linking.canOpenURL(phoneUrl)
    .then(supported => {
      if (supported) {
        Linking.openURL(phoneUrl)
      } else {
        console.log('Unable to dial the number.')
      }
    })
    .catch(err => console.error('An error occurred', err))
}

export const WINDOW_WIDTH = Dimensions.get('window').width
export const WINDOW_HEIGHT = Dimensions.get('window').height
export const SCREEN_WIDTH = Dimensions.get('screen').width
export const SCREEN_HEIGHT = Dimensions.get('screen').height

export const showToast = (
  message: string,
  duration: number = ToastAndroid.SHORT,
) => ToastAndroid.show(message, duration)

export const defaultBackPressCallback = () => {
  const back = navigationRef.canGoBack()
  if (back) {
    navigationRef.goBack()
  } else {
    goDefaultPlatformBack()
  }
  return true
}

export const goDefaultPlatformBack = () => {
  if (Platform.OS === 'web') {
    if (window !== undefined) {
      if (window.history.length > 1) {
        window.history.back()
      } else {
        window.history.replaceState({}, '/home')
      }
    }
  } else finishCurrentActivity()
}

export function postMessageToWebView(window: Window, data: object) {
  try {
    ;(window as any).ReactNativeWebView.postMessage(JSON.stringify(data))
  } catch (error) {
    Sentry.captureException(error)
  }
}

export enum WEBVIEW_MESSAGE_CODES {
  ERROR = 'ERR_001',
  INFO = 'INFO_001',
  TITLE = 'TITLE',
  REFRESH_TOKEN = 'refreshToken',
  REPORT_CLICK = 'reportClick',
  PROMOTIONS = 'promotionsClick',
  GREETINGS = 'greetingsClick',
  PRINT_PDF = 'printPDF',
  CLOSE_APP = 'closeApp',
  BACK_CLICK = 'backClick',
}

export function withAsyncCooldown<T extends (...args: any[]) => Promise<any>>(
  handler: T,
) {
  let locked = false

  return async function (...args: Parameters<T>) {
    if (locked) return

    locked = true
    try {
      await handler(...args)
    } finally {
      locked = false
    }
  }
}
